import {
  BLOCKS_VIEW_NODE_PAD,
  BLOCKS_VIEW_MULTILABEL_PADDING_HORIZONTAL,
  BLOCKS_VIEW_LEAF_LABEL_MARGIN,
  BLOCKS_VIEW_LEAF_RD_INNERSIZE,
  BLOCKS_VIEW_NODE_LABEL_LINE_HEIGHT,
} from '../../consts';
import { type BlocksViewNode } from '../../types';
import { rectWidth } from '../../misc/geometry';
import { wrapString } from '../../misc/string';
import { labelRect } from './labelRect';
import { collapsedGroupHeaderBottom, measureNodeLabel } from '../util';

/** @returns true if the node is a leaf node or a collapsed component group. */
const isLeaflike = ({ children, open, isComponent }: BlocksViewNode) =>
  !children?.length || (!open && isComponent);

/** @returns the width available for the main label. */
const mainLabelSpace = (node: BlocksViewNode) =>
  isLeaflike(node)
    ? rectWidth(node.bounds) -
      (BLOCKS_VIEW_NODE_PAD + BLOCKS_VIEW_NODE_PAD) -
      2 * BLOCKS_VIEW_LEAF_LABEL_MARGIN
    : rectWidth(labelRect(node));

/** @returns the width available for the group sublabel. */
const subLabelSpace = (node: BlocksViewNode) =>
  isLeaflike(node) ? mainLabelSpace(node) : rectWidth(node.bounds);

/** @returns the width available for the text of multilabels. */
const multiLabelSpace = (node: BlocksViewNode) =>
  rectWidth(node.bounds) -
  (BLOCKS_VIEW_NODE_PAD + BLOCKS_VIEW_NODE_PAD) -
  2 * BLOCKS_VIEW_MULTILABEL_PADDING_HORIZONTAL;

/** @returns the height available for the main label. */
const availableLabelHeight = ({
  cell,
  children,
  isComponent,
}: BlocksViewNode) => {
  if (!cell) {
    return 0;
  }
  const [, top, , bottom] = cell;
  const isGroup = children?.length;
  if (isGroup) {
    if (isComponent) {
      const bodyHeight = bottom - collapsedGroupHeaderBottom(cell);
      return (
        bodyHeight - 2 * BLOCKS_VIEW_NODE_PAD - BLOCKS_VIEW_LEAF_RD_INNERSIZE
      );
    }
    const bodyHeight = bottom - top;
    return bodyHeight - 2 * BLOCKS_VIEW_NODE_PAD;
  }
  const bodyHeight = bottom - top;
  return bodyHeight - 2 * BLOCKS_VIEW_NODE_PAD - BLOCKS_VIEW_LEAF_RD_INNERSIZE;
};

/** @returns the maximum number of lines available for the main label, based on the available height in the node. */
const mainLabelMaxLines = (node: BlocksViewNode) => {
  const {
    cell,
    children,
    open,
    labels: [, , ...multiLabels],
  } = node;
  if (!cell) {
    return 3;
  }
  const isGroup = children?.length;
  if (isGroup && open) {
    return 3;
  }
  if (multiLabels.length) {
    // how the main label should share the height with multilabels is undefined behavior. thus, I am leaving the default 3 lines if any other labels are present.
    return 3;
  }

  return Math.floor(
    availableLabelHeight(node) / BLOCKS_VIEW_NODE_LABEL_LINE_HEIGHT
  );
};

/** @returns the node's displayed labels, wrapped based on the available size and properties of the node. */
const nodeDisplayLabel = (node: BlocksViewNode) => {
  const {
    labels: [mainLabel, subLabel, ...multiLabels],
  } = node;
  const multiLabelsWidth = multiLabelSpace(node);
  const mainDisplayLabel = wrapString(
    mainLabel,
    mainLabelSpace(node),
    measureNodeLabel,
    mainLabelMaxLines(node)
  );
  const subDisplayLabel = wrapString(
    subLabel,
    subLabelSpace(node),
    measureNodeLabel
  );
  const multiDisplayLabels = multiLabels.map(label =>
    wrapString(label, multiLabelsWidth, measureNodeLabel)
  );
  return [mainDisplayLabel, subDisplayLabel, ...multiDisplayLabels];
};

export { nodeDisplayLabel };
